import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassEmptyState,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { ClipboardList, Eye, ListChecks, Pencil, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { GET_CHECKLIST_TEMPLATES } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface TemplateFieldNode {
  id: number;
  seq: number;
  fieldName: string;
  fieldType: string;
  required: number | null;
  group: string | null;
}

interface TemplateNode {
  id: number;
  name: string;
  description: string | null;
  jobType: string | null;
  version: number | null;
  active: number | null;
  fields: TemplateFieldNode[];
}

interface TemplateEdge {
  node: TemplateNode;
}

interface TemplateConnection {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: TemplateEdge[];
}

interface GetTemplatesData {
  checklistTemplates: TemplateConnection;
}

/* ── Constants ── */

type JobTypeFilter = 'All' | 'inspection' | 'maintenance' | 'turnover' | 'pest_control';

const jobTypeOptions: { value: JobTypeFilter; label: string }[] = [
  { value: 'All', label: 'All Job Types' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'turnover', label: 'Turnover' },
  { value: 'pest_control', label: 'Pest Control' },
];

function jobTypeBadgeVariant(jobType: string): 'info' | 'warning' | 'default' | 'success' {
  switch (jobType) {
    case 'inspection':
      return 'info';
    case 'maintenance':
      return 'warning';
    case 'pest_control':
      return 'success';
    default:
      return 'default';
  }
}

function formatJobTypeLabel(jobType: string): string {
  return jobType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/* ── Page ── */

export function ChecklistTemplatesPage(): ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState<JobTypeFilter>('All');

  const { data, loading, error } = useQuery<GetTemplatesData>(GET_CHECKLIST_TEMPLATES, {
    variables: {
      first: 100,
      ...(jobTypeFilter !== 'All' ? { jobType: jobTypeFilter } : {}),
    },
  });

  const templates: TemplateNode[] = data?.checklistTemplates.edges.map((e) => e.node) ?? [];

  const filtered = templates.filter((t) => {
    const query = search.toLowerCase();
    if (query === '') {
      return true;
    }
    const name = t.name.toLowerCase();
    const description = (t.description ?? '').toLowerCase();
    const jobType = (t.jobType ?? '').toLowerCase();
    return name.includes(query) || description.includes(query) || jobType.includes(query);
  });

  const totalTemplates = data?.checklistTemplates.totalCount ?? templates.length;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Manage reusable checklist templates for service orders
        </p>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            void navigate('/checklist-templates/new');
          }}
        >
          <Plus size={16} />
          New Template
        </GlassButton>
      </div>

      {/* Stats */}
      <GlassCard>
        <GlassCardContent className="flex items-center gap-4 py-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-primary-soft">
            <ListChecks size={20} className="text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-semibold text-text leading-none">
              {loading ? (
                <SkeletonLoader className="w-12 h-7" />
              ) : (
                <AnimatedCounter value={totalTemplates} />
              )}
            </p>
            <p className="text-xs text-text-secondary mt-1 truncate">Total Templates</p>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Table */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">All Templates</h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
                />
                <GlassInput
                  placeholder="Search templates…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  className="pl-8 w-full sm:w-56"
                  aria-label="Search templates"
                />
              </div>
              <GlassSelect
                options={jobTypeOptions}
                value={jobTypeFilter}
                onChange={(e) => {
                  setJobTypeFilter(e.target.value as JobTypeFilter);
                }}
                aria-label="Filter by job type"
                className="w-full sm:w-44"
              />
            </div>
          </div>
        </GlassCardHeader>

        {loading ? (
          <div className="p-5 space-y-3">
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
          </div>
        ) : error !== undefined ? (
          <div className="py-12 text-center text-sm text-danger">
            Failed to load templates: {error.message}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8">
            <GlassEmptyState
              icon={ClipboardList}
              title="No templates found"
              description={
                search !== '' || jobTypeFilter !== 'All'
                  ? 'Try adjusting your search or filter to find what you are looking for.'
                  : 'There are no checklist templates yet. Create one to get started.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <GlassTable>
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell>Template</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden sm:table-cell">
                    Job Type
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden md:table-cell">
                    Fields
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell className="hidden sm:table-cell">
                    Status
                  </GlassTableHeaderCell>
                  <GlassTableHeaderCell aria-label="Actions" />
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {filtered.map((template) => (
                  <GlassTableRow
                    key={template.id}
                    className="cursor-pointer"
                    onClick={() => {
                      void navigate(`/checklist-templates/${String(template.id)}/edit`);
                    }}
                  >
                    <GlassTableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-text">{template.name}</span>
                        {template.description !== null && (
                          <span className="text-xs text-text-secondary truncate max-w-[260px]">
                            {template.description}
                          </span>
                        )}
                      </div>
                    </GlassTableCell>
                    <GlassTableCell className="hidden sm:table-cell">
                      {template.jobType !== null ? (
                        <GlassBadge variant={jobTypeBadgeVariant(template.jobType)}>
                          {formatJobTypeLabel(template.jobType)}
                        </GlassBadge>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </GlassTableCell>
                    <GlassTableCell className="hidden md:table-cell">
                      <span className="flex items-center gap-1 text-text-secondary">
                        <Eye size={12} className="shrink-0" />
                        {template.fields.length}
                      </span>
                    </GlassTableCell>
                    <GlassTableCell className="hidden sm:table-cell">
                      {template.active === 1 ? (
                        <GlassBadge variant="success">Active</GlassBadge>
                      ) : (
                        <GlassBadge variant="default">Inactive</GlassBadge>
                      )}
                    </GlassTableCell>
                    <GlassTableCell>
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        aria-label={`Edit ${template.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          void navigate(`/checklist-templates/${String(template.id)}/edit`);
                        }}
                      >
                        <Pencil size={14} />
                      </GlassButton>
                    </GlassTableCell>
                  </GlassTableRow>
                ))}
              </GlassTableBody>
            </GlassTable>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
